import { useState, useEffect, useCallback } from 'react';

export function isDarkMode(): boolean {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        return document.documentElement.classList.contains('dark');
    }
    return false;
}

export function resetDarkMode() {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        document.documentElement.classList.remove('dark');
    }
}

function getIsDark(): boolean {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem('theme');
    if (stored === 'dark') return true;
    if (stored === 'light') return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function useDarkMode(): boolean {
    const [isDark, setIsDark] = useState(isDarkMode);

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDark(isDarkMode());
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });

        return () => observer.disconnect();
    }, []);

    return isDark;
}

export function useToggleDarkMode(): { isDark: boolean; toggle: () => void } {
    const [isDark, setIsDark] = useState(getIsDark);

    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDark(getIsDark());
        });

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class'],
        });

        return () => observer.disconnect();
    }, []);

    const toggle = useCallback(() => {
        const next = !isDark;
        setIsDark(next);
        localStorage.setItem('theme', next ? 'dark' : 'light');
        document.documentElement.classList.toggle('dark', next);
        document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', next ? 'dark' : 'light');
        document.querySelector('meta[name="theme-color"]')?.setAttribute('content', next ? '#000000' : '#ffffff');
    }, [isDark]);

    return { isDark, toggle };
}
